import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  useTheme,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import TableViewIcon from "@mui/icons-material/TableChart";
import FolderIcon from "@mui/icons-material/Folder";
import CardViewIcon from "@mui/icons-material/ViewModule";
import SettingsIcon from "@mui/icons-material/Settings";
import { useSearchParams } from "react-router-dom";
import SidebarLayout from "../../shared/sections/sidebar/SidebarLayout";
import TitleSection from "../../shared/sections/TitleSection";
import { apiFetch } from "../../api/apiClient";

export default function CalculationResultsPage() {
  const [searchParams] = useSearchParams();
  const requestedProjectId = searchParams.get("pid") || "";
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
    border: "#2D2F31",
  };

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [calculationResults, setCalculationResults] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const resizingColumn = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const [projectIds, setProjectIds] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [viewMode, setViewMode] = useState("table"); // "table" or "card"
  const [topLevelTab, setTopLevelTab] = useState(0); // 0: Calculation Results, 1: Analysis

  // Analysis-specific state
  const [selectedProjects, setSelectedProjects] = useState([]); // Array of project IDs for comparison
  const [comparisonData, setComparisonData] = useState({}); // Holds data for all selected projects
  const [analysisTab, setAnalysisTab] = useState(0); // Active tab in Analysis view
  const [availableColumns, setAvailableColumns] = useState([]); // All columns available for current table
  const [selectedColumns, setSelectedColumns] = useState([]); // User-selected columns to display
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false); // Column selection dialog

  // Pivot table configuration
  const [pivotConfig, setPivotConfig] = useState({
    rows: [], // Columns to use as row identifiers
    columns: [], // Columns to pivot into column headers
    values: [], // Columns to aggregate/sum
  });
  const [pivotConfigOpen, setPivotConfigOpen] = useState(false); // Pivot config dialog
  const [projectSelectorOpen, setProjectSelectorOpen] = useState(false); // Project selector dialog

  // Tab configuration matching result sets from database
  const resultTabs = [
    { id: "description", label: "Description", key: "DescriptionResult" },
    { id: "formulation", label: "Formulation", key: "FormulationResult" },
    { id: "goodsReturn", label: "Goods Return", key: "GoodsReturnResult" },
    { id: "material", label: "Material", key: "MaterialResult" },
    { id: "process", label: "Process", key: "ProcessResult" },
    { id: "packaging", label: "Packaging", key: "PackagingResult" },
    { id: "logistics", label: "Logistics", key: "LogisticsResult" },
    { id: "bom", label: "BOM", key: "BomResult" },
    { id: "shouldCost", label: "Should Cost", key: "ShouldCostResult" },
  ];

  useEffect(() => {
    loadProjectIds();
  }, [requestedProjectId]);

  useEffect(() => {
    if (selectedProjectId) {
      loadCalculationResults(selectedProjectId);
    }
  }, [selectedProjectId]);

  // Cleanup event listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleMouseMove = (e) => {
    if (resizingColumn.current) {
      const diff = e.clientX - startX.current;
      const newWidth = Math.max(80, startWidth.current + diff);
      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn.current]: newWidth,
      }));
    }
  };

  const handleMouseUp = () => {
    resizingColumn.current = null;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const loadProjectIds = async () => {
    setLoadingProjects(true);
    try {
      const response = await apiFetch("/api/fb/v2/get-project-ids");

      if (!response.ok) {
        throw new Error("Failed to load project IDs");
      }

      const data = await response.json();
      const projects = data.project_ids || [];
      setProjectIds(projects);

      const requestedProjectExists = requestedProjectId
        ? projects.some((project) => project.ProjectID === requestedProjectId)
        : false;
      const initialProjectId = requestedProjectExists
        ? requestedProjectId
        : requestedProjectId || projects[0]?.ProjectID;

      // Auto-select requested project from query param, else fallback to latest item in list
      if (projects.length > 0) {
        setSelectedProjectId(initialProjectId || projects[0].ProjectID);

        const secondaryProject = projects.find(
          (project) => project.ProjectID !== (initialProjectId || projects[0].ProjectID),
        );

        // Also initialize selectedProjects for comparison
        if (secondaryProject) {
          setSelectedProjects([
            initialProjectId || projects[0].ProjectID,
            secondaryProject.ProjectID,
          ]);
        } else {
          setSelectedProjects([initialProjectId || projects[0].ProjectID]);
        }
      }
    } catch (err) {
      setError(err.message);
      console.error("Error loading project IDs:", err);
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadCalculationResults = async (projectId) => {
    if (!projectId) return;

    setLoading(true);
    setError(null);
    try {
      // Fetch calculation results from database using ProjectID
      const response = await apiFetch(`/api/fb/v2/fetchCostingData?pid=${projectId}`);

      if (!response.ok) {
        throw new Error("Failed to load calculation results");
      }

      const data = await response.json();
      setCalculationResults(data.calculation_result);
    } catch (err) {
      setError(err.message);
      console.error("Error loading results:", err);
    } finally {
      setLoading(false);
    }
  };

  // Load comparison data for multiple projects
  const loadComparisonData = async (projectIds) => {
    if (!projectIds || projectIds.length === 0) return;

    setLoading(true);
    setError(null);
    const allData = {};

    try {
      // Fetch data for each project in parallel
      const promises = projectIds.map(async (pid) => {
        const response = await apiFetch(`/api/fb/v2/fetchCostingData?pid=${pid}`);
        if (!response.ok) {
          throw new Error(`Failed to load data for ${pid}`);
        }
        const data = await response.json();
        return { projectId: pid, data: data.calculation_result };
      });

      const results = await Promise.all(promises);

      results.forEach(({ projectId, data }) => {
        allData[projectId] = data;
      });

      setComparisonData(allData);

      // Set available columns from first project's active tab
      if (results.length > 0) {
        const firstProjectData = results[0].data;
        const currentTable = resultTabs[analysisTab].key;
        if (
          firstProjectData &&
          firstProjectData[currentTable] &&
          firstProjectData[currentTable].length > 0
        ) {
          const columns = Object.keys(firstProjectData[currentTable][0]);
          setAvailableColumns(columns);

          // Reset selected columns when switching tables OR if none selected
          // Keep first column (row identifier) always selected, plus 2 more data columns
          const defaultColumns = columns.slice(0, Math.min(3, columns.length));
          setSelectedColumns(defaultColumns);

          // Always reset pivot configuration for the new table
          const numericColumns = columns.filter((col, idx) => {
            if (idx === 0) return false; // Skip first column
            const sampleValue = firstProjectData[currentTable][0][col];
            return typeof sampleValue === "number";
          });

          // Set default pivot configuration based on current table
          if (currentTable === "FormulationResult") {
            // Formulation-specific defaults
            setPivotConfig({
              rows: columns.includes("Ingredient") ? ["Ingredient"] : ["ProjectID"],
              columns: ["ProjectID"],
              values: ["IngredientCost", "Input"].filter((col) => columns.includes(col)),
            });
          } else if (currentTable === "BomResult") {
            // BOM-specific defaults
            setPivotConfig({
              rows: columns.includes("PartKey") ? ["PartKey"] : [],
              columns: ["ProjectID"],
              values: ["MaterialCost", "ShouldCost"].filter((col) => columns.includes(col)),
            });
          } else if (currentTable === "DescriptionResult") {
            // Description-specific defaults
            setPivotConfig({
              rows: columns.includes("PartKey") ? ["PartKey"] : [],
              columns: ["ProjectID"],
              values: ["AnnualVolume", "BatchQuantity"].filter((col) => columns.includes(col)),
            });
          } else if (currentTable === "GoodsReturnResult") {
            // Goods Return-specific defaults
            setPivotConfig({
              rows: columns.includes("PartKey") ? ["PartKey"] : [],
              columns: ["ProjectID"],
              values: ["CostPerKg"].filter((col) => columns.includes(col)),
            });
          } else if (currentTable === "MaterialResult") {
            // Material-specific defaults
            setPivotConfig({
              rows: columns.includes("PartKey") ? ["PartKey"] : [],
              columns: ["ProjectID"],
              values: ["MaterialCost"].filter((col) => columns.includes(col)),
            });
          } else if (currentTable === "ProcessResult") {
            // Process-specific defaults
            setPivotConfig({
              rows: ["Process", "ProcessName"].filter((col) => columns.includes(col)),
              columns: ["ProjectID"],
              values: ["ProcessCost"].filter((col) => columns.includes(col)),
            });
          } else if (currentTable === "PackagingResult") {
            // Packaging-specific defaults
            setPivotConfig({
              rows: ["PartKey", "PackagingMaterial", "PackagingName"].filter((col) =>
                columns.includes(col)
              ),
              columns: ["ProjectID"],
              values: ["PackagingCost"].filter((col) => columns.includes(col)),
            });
          } else {
            // Default for other tables: ProjectID as row, no column pivoting
            setPivotConfig({
              rows: ["ProjectID"],
              columns: [],
              values: numericColumns.slice(0, 3), // First 3 numeric columns
            });
          }
        }
      }
    } catch (err) {
      setError(err.message);
      console.error("Error loading comparison data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Effect to load comparison data when projects change
  useEffect(() => {
    if (topLevelTab === 1 && selectedProjects.length > 0) {
      loadComparisonData(selectedProjects);
    }
  }, [selectedProjects, topLevelTab, analysisTab]);

  const handleRefresh = async () => {
    // Refresh both project list and current results
    await loadProjectIds();
    if (selectedProjectId) {
      await loadCalculationResults(selectedProjectId);
    }
    if (topLevelTab === 1 && selectedProjects.length > 0) {
      await loadComparisonData(selectedProjects);
    }
  };

  const handleDownloadCSV = (resultKey) => {
    if (!calculationResults || !calculationResults[resultKey]) return;

    const data = calculationResults[resultKey];
    if (data.length === 0) return;

    // Convert to CSV
    const headers = Object.keys(data[0]).join(",");
    const rows = data.map((row) =>
      Object.values(row)
        .map((val) => `"${val}"`)
        .join(",")
    );
    const csv = [headers, ...rows].join("\n");

    // Download
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${resultKey}_${new Date().toISOString()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const renderCardView = (resultKey) => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!calculationResults) {
      return (
        <Alert severity="info" sx={{ m: 2 }}>
          {selectedProjectId
            ? "No calculation results available for selected project."
            : "Select a project to view calculation results."}
        </Alert>
      );
    }

    const data = calculationResults[resultKey];

    if (!data || data.length === 0) {
      return (
        <Alert severity="info" sx={{ m: 2 }}>
          No data available for {resultKey}
        </Alert>
      );
    }

    const columns = Object.keys(data[0]);

    return (
      <Box
        sx={{
          p: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          width: "95vw",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: 2,
            pt: 1,
            pb: 1,
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: perlColors.textPrimary, fontWeight: 500 }}>
            {resultTabs[activeTab].label} ({data.length} records)
          </Typography>
          <Tooltip title="Download as CSV">
            <IconButton
              onClick={() => handleDownloadCSV(resultKey)}
              sx={{ color: perlColors.textPrimary }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            flex: 1,
            px: 2,
            pb: 2,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card
            sx={{
              bgcolor: perlColors.background,
              border: `1px solid ${perlColors.border}`,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <CardContent
              sx={{
                p: 2,
                "&:last-child": { pb: 2 },
                flex: 1,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <TableContainer sx={{ flex: 1, overflowX: "auto", overflowY: "auto" }}>
                <Table
                  size="small"
                  sx={{ tableLayout: "fixed", width: "800px", minWidth: "800px" }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          color: perlColors.textPrimary,
                          fontWeight: 700,
                          borderBottom: `2px solid ${perlColors.border}`,
                          padding: "12px",
                          fontSize: "0.875rem",
                          whiteSpace: "nowrap",
                          bgcolor: "#2D2F31",
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          width: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        Parameter
                      </TableCell>
                      {data.map((_, idx) => (
                        <TableCell
                          key={idx}
                          sx={{
                            color: perlColors.textPrimary,
                            fontWeight: 700,
                            borderBottom: `2px solid ${perlColors.border}`,
                            padding: "12px",
                            fontSize: "0.875rem",
                            textAlign: "center",
                            bgcolor: "#2D2F31",
                            whiteSpace: "nowrap",
                            width: "300px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {data.length === 1 ? "Value" : `Record ${idx + 1}`}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {columns.map((col) => (
                      <TableRow
                        key={col}
                        sx={{
                          "&:hover": { bgcolor: "rgba(255, 255, 255, 0.05)" },
                        }}
                      >
                        <TableCell
                          sx={{
                            color: perlColors.textPrimary,
                            fontWeight: 600,
                            borderBottom: `1px solid ${perlColors.border}`,
                            padding: "10px 12px",
                            fontSize: "0.875rem",
                            whiteSpace: "nowrap",
                            bgcolor: perlColors.background,
                            position: "sticky",
                            left: 0,
                            zIndex: 1,
                            width: "200px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={col}
                        >
                          {col}
                        </TableCell>
                        {data.map((row, idx) => (
                          <TableCell
                            key={idx}
                            sx={{
                              color: perlColors.textPrimary,
                              borderBottom: `1px solid ${perlColors.border}`,
                              padding: "10px 12px",
                              fontSize: "0.875rem",
                              textAlign: data.length === 1 ? "left" : "center",
                              width: "300px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={
                              typeof row[col] === "number"
                                ? row[col].toLocaleString(undefined, {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 4,
                                  })
                                : row[col]?.toString() || "-"
                            }
                          >
                            {typeof row[col] === "number"
                              ? row[col].toLocaleString(undefined, {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 4,
                                })
                              : row[col]?.toString() || "-"}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  };

  const renderTable = (resultKey) => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!calculationResults) {
      return (
        <Alert severity="info" sx={{ m: 2 }}>
          {selectedProjectId
            ? "No calculation results available for selected project."
            : "Select a project to view calculation results."}
        </Alert>
      );
    }

    const data = calculationResults[resultKey];

    if (!data || data.length === 0) {
      return (
        <Alert severity="info" sx={{ m: 2 }}>
          No data available for {resultKey}
        </Alert>
      );
    }

    const columns = Object.keys(data[0]);

    // Standard column widths based on data type and common patterns
    const getColumnWidth = (col) => {
      // Use stored width if available (after user resize)
      if (columnWidths[`${resultKey}_${col}`]) {
        return columnWidths[`${resultKey}_${col}`];
      }

      // Standard widths based on column name patterns
      const colLower = col.toLowerCase();

      // ID columns - narrow
      if (colLower.includes("id") || colLower === "projectid" || colLower === "projectcode") {
        return 120;
      }

      // Name/Description columns - wide
      if (
        colLower.includes("name") ||
        colLower.includes("description") ||
        colLower.includes("product")
      ) {
        return 200;
      }

      // Cost/Price/Amount columns - medium
      if (colLower.includes("cost") || colLower.includes("price") || colLower.includes("amount")) {
        return 140;
      }

      // Percentage columns - narrow
      if (colLower.includes("percent") || colLower.includes("%") || colLower.includes("per")) {
        return 110;
      }

      // Quantity/Weight/Volume columns - medium
      if (
        colLower.includes("qty") ||
        colLower.includes("quantity") ||
        colLower.includes("weight") ||
        colLower.includes("volume")
      ) {
        return 120;
      }

      // Date columns - medium
      if (colLower.includes("date") || colLower.includes("time")) {
        return 150;
      }

      // Default width for all other columns
      return 140;
    };

    // Mouse down handler for column resize
    const handleMouseDown = (e, col) => {
      e.preventDefault();
      resizingColumn.current = `${resultKey}_${col}`;
      startX.current = e.clientX;
      startWidth.current = getColumnWidth(col);
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    return (
      <Box sx={{ p: 0, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: 2,
            pt: 1,
            pb: 1,
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: perlColors.textPrimary, fontWeight: 500 }}>
            {resultTabs[activeTab].label} ({data.length} rows)
          </Typography>
          <Tooltip title="Download as CSV">
            <IconButton
              onClick={() => handleDownloadCSV(resultKey)}
              sx={{ color: perlColors.textPrimary }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            bgcolor: perlColors.background,
            border: `1px solid ${perlColors.border}`,
            flex: 1,
            overflow: "auto",
            width: "1400px",
            minHeight: 0,
            maxHeight: "80%",
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              width: "100%",
              tableLayout: "auto",
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col}
                    sx={{
                      bgcolor: "#2D2F31",
                      color: perlColors.textPrimary,
                      fontWeight: 600,
                      borderBottom: `2px solid ${perlColors.border}`,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: getColumnWidth(col),
                      minWidth: getColumnWidth(col),
                      maxWidth: getColumnWidth(col),
                      position: "relative",
                      padding: "6px 16px",
                    }}
                  >
                    {col}
                    {/* Resize handle */}
                    <Box
                      onMouseDown={(e) => handleMouseDown(e, col)}
                      sx={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: "5px",
                        cursor: "col-resize",
                        userSelect: "none",
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.2)",
                        },
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    height: "36px",
                    "&:hover": { bgcolor: "rgba(255, 255, 255, 0.05)" },
                  }}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col}
                      sx={{
                        color: perlColors.textPrimary,
                        borderBottom: `1px solid ${perlColors.border}`,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: getColumnWidth(col),
                        minWidth: getColumnWidth(col),
                        maxWidth: getColumnWidth(col),
                        height: "36px",
                        padding: "6px 16px",
                      }}
                      title={
                        typeof row[col] === "number"
                          ? row[col].toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 4,
                            })
                          : row[col]?.toString() || "-"
                      }
                    >
                      {typeof row[col] === "number"
                        ? row[col].toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 4,
                          })
                        : row[col]?.toString() || "-"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Build pivot table from comparison data
  const buildPivotTable = () => {
    if (!comparisonData || Object.keys(comparisonData).length === 0) {
      return { rows: [], columnHeaders: [], rowFields: [], colFields: [], valueFields: [] };
    }

    const currentTable = resultTabs[analysisTab].key;
    const { rows: rowFields, columns: colFields, values: valueFields } = pivotConfig;

    // Collect all data from all projects
    const allData = [];
    selectedProjects.forEach((projectId) => {
      const projData = comparisonData[projectId];
      if (projData && projData[currentTable]) {
        projData[currentTable].forEach((row) => {
          allData.push({ ...row, ProjectID: projectId });
        });
      }
    });

    if (allData.length === 0) {
      return { rows: [], columnHeaders: [], rowFields, colFields, valueFields };
    }

    // If no row fields selected, default to ProjectID
    const effectiveRowFields = rowFields.length > 0 ? rowFields : ["ProjectID"];
    const effectiveColFields = colFields;
    const effectiveValueFields = valueFields.length > 0 ? valueFields : [];

    // Build row groups
    const rowGroupsMap = new Map();

    allData.forEach((dataRow) => {
      // Create row key from row fields
      const rowKey = effectiveRowFields.map((field) => dataRow[field] || "").join("|");

      if (!rowGroupsMap.has(rowKey)) {
        const rowGroup = {};
        effectiveRowFields.forEach((field) => {
          rowGroup[field] = dataRow[field];
        });
        rowGroupsMap.set(rowKey, rowGroup);
      }

      const rowGroup = rowGroupsMap.get(rowKey);

      // If no column fields, aggregate values directly
      if (effectiveColFields.length === 0) {
        effectiveValueFields.forEach((valueField) => {
          if (!rowGroup[valueField]) {
            rowGroup[valueField] = 0;
          }
          const val = parseFloat(dataRow[valueField]);
          if (!isNaN(val)) {
            rowGroup[valueField] += val;
          }
        });
      } else {
        // Create column key from column fields
        const colKey = effectiveColFields.map((field) => dataRow[field] || "").join("|");

        // Aggregate values for this row-column combination
        effectiveValueFields.forEach((valueField) => {
          const cellKey = `${colKey}__${valueField}`;
          if (!rowGroup[cellKey]) {
            rowGroup[cellKey] = 0;
          }
          const val = parseFloat(dataRow[valueField]);
          if (!isNaN(val)) {
            rowGroup[cellKey] += val;
          }
        });
      }
    });

    const pivotRows = Array.from(rowGroupsMap.values());

    // Build column headers (for column fields)
    let columnHeaders = [];
    if (effectiveColFields.length > 0) {
      const colValuesSet = new Set();
      allData.forEach((dataRow) => {
        const colKey = effectiveColFields.map((field) => dataRow[field] || "").join("|");
        colValuesSet.add(colKey);
      });
      columnHeaders = Array.from(colValuesSet);
    }

    // Calculate deltas
    if (selectedProjects.length > 1 || columnHeaders.length > 1) {
      if (effectiveColFields.length === 0) {
        // No column pivoting - deltas between rows (projects)
        // Use first project as reference
        const firstProjectId = selectedProjects[0];
        const firstProjectRow = pivotRows.find((row) => row.ProjectID === firstProjectId);

        if (firstProjectRow) {
          pivotRows.forEach((row) => {
            if (row.ProjectID !== firstProjectId) {
              effectiveValueFields.forEach((valueField) => {
                const currentValue = row[valueField];
                const firstValue = firstProjectRow[valueField];
                if (typeof currentValue === "number" && typeof firstValue === "number") {
                  row[`${valueField}_delta`] = currentValue - firstValue;
                }
              });
            }
          });
        }
      } else {
        // Column pivoting - deltas between columns
        // Use first column header as reference
        const firstColumnHeader = columnHeaders[0];

        pivotRows.forEach((row) => {
          // For each value field, calculate delta between first column and other columns
          effectiveValueFields.forEach((valueField) => {
            const firstColumnKey = `${firstColumnHeader}__${valueField}`;
            const firstValue = row[firstColumnKey];

            // Calculate delta for each subsequent column
            columnHeaders.slice(1).forEach((colHeader) => {
              const cellKey = `${colHeader}__${valueField}`;
              const currentValue = row[cellKey];

              if (typeof currentValue === "number" && typeof firstValue === "number") {
                row[`${cellKey}_delta`] = currentValue - firstValue;
              }
            });
          });
        });
      }
    }

    return {
      rows: pivotRows,
      columnHeaders,
      rowFields: effectiveRowFields,
      colFields: effectiveColFields,
      valueFields: effectiveValueFields,
    };
  };

  // Render comparison view for Analysis tab
  const renderComparisonView = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      );
    }

    if (selectedProjects.length === 0) {
      return (
        <Alert severity="info" sx={{ m: 2 }}>
          Please select at least one project to view comparison analysis.
        </Alert>
      );
    }

    if (Object.keys(comparisonData).length === 0) {
      return (
        <Alert severity="info" sx={{ m: 2 }}>
          Loading comparison data...
        </Alert>
      );
    }

    const currentTable = resultTabs[analysisTab].key;
    const firstProjectId = selectedProjects[0];
    const firstProjectData = comparisonData[firstProjectId];

    if (!firstProjectData || !firstProjectData[currentTable]) {
      return (
        <Alert severity="info" sx={{ m: 2 }}>
          No data available for {resultTabs[analysisTab].label}
        </Alert>
      );
    }

    const tableData = firstProjectData[currentTable];
    if (tableData.length === 0) {
      return (
        <Alert severity="info" sx={{ m: 2 }}>
          No data available for {resultTabs[analysisTab].label}
        </Alert>
      );
    }

    // Build pivot table
    const pivotTable = buildPivotTable();
    const { rows: pivotRows, columnHeaders, rowFields, colFields, valueFields } = pivotTable;

    if (pivotRows.length === 0) {
      return (
        <Alert severity="info" sx={{ m: 2 }}>
          No data to display. Please configure pivot settings.
        </Alert>
      );
    }

    return (
      <Box
        sx={{
          p: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: 2,
            pt: 1,
            pb: 1,
            alignItems: "center",
            flexShrink: 0,
            width: "1450px",
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ color: perlColors.textPrimary, fontWeight: 500, flex: 1, minWidth: 0 }}
          >
            {resultTabs[analysisTab].label} Analysis ({selectedProjects.length} projects,{" "}
            {pivotRows.length} rows)
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <Tooltip title="Select Projects">
              <IconButton
                onClick={() => setProjectSelectorOpen(true)}
                size="small"
                sx={{ color: perlColors.textPrimary }}
              >
                <Badge badgeContent={selectedProjects.length} color="error">
                  <FolderIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title="Configure Pivot Table">
              <IconButton
                onClick={() => setPivotConfigOpen(true)}
                size="small"
                sx={{ color: perlColors.textPrimary }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download as CSV">
              <IconButton
                onClick={() => {
                  // Export pivot data
                  const headers = Object.keys(pivotRows[0]).join(",");
                  const rows = pivotRows.map((row) =>
                    Object.values(row)
                      .map((val) => `"${val}"`)
                      .join(",")
                  );
                  const csv = [headers, ...rows].join("\n");
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `pivot_${
                    resultTabs[analysisTab].key
                  }_${new Date().toISOString()}.csv`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                }}
                sx={{ color: perlColors.textPrimary }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            bgcolor: perlColors.background,
            border: `1px solid ${perlColors.border}`,
            flex: 1,
            overflowX: "auto",
            overflowY: "auto",
            minHeight: 0,
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: "max-content" }}>
            <TableHead>
              <TableRow>
                {/* Row field headers (e.g., ProjectID, Ingredient, etc.) */}
                {rowFields.map((field) => (
                  <TableCell
                    key={field}
                    sx={{
                      bgcolor: "#2D2F31",
                      color: perlColors.textPrimary,
                      fontWeight: 600,
                      borderBottom: `2px solid ${perlColors.border}`,
                      position: "sticky",
                      left: 0,
                      zIndex: 3,
                      minWidth: 120,
                      maxWidth: 150,
                      padding: "8px 12px",
                    }}
                  >
                    {field}
                  </TableCell>
                ))}

                {/* Column pivot headers or value fields */}
                {colFields.length > 0
                  ? // Group by value field first, then show all projects, then delta
                    valueFields.flatMap((valField) => [
                      // Show all project columns for this value field
                      ...columnHeaders.map((colHeader) => (
                        <TableCell
                          key={`${colHeader}__${valField}`}
                          sx={{
                            bgcolor: "#2D2F31",
                            color: perlColors.textPrimary,
                            fontWeight: 600,
                            borderBottom: `2px solid ${perlColors.border}`,
                            whiteSpace: "nowrap",
                            minWidth: 80,
                            maxWidth: 100,
                            padding: "6px 8px",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, fontSize: "0.8rem", lineHeight: 1.2 }}
                          >
                            {valField}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "0.65rem", opacity: 0.8, display: "block" }}
                          >
                            {colHeader}
                          </Typography>
                        </TableCell>
                      )),
                      // Then show delta column for subsequent projects
                      ...columnHeaders.slice(1).map((colHeader) => (
                        <TableCell
                          key={`${colHeader}__${valField}_delta`}
                          sx={{
                            bgcolor: "#3D3F41",
                            color: "#ff6034",
                            fontWeight: 600,
                            borderBottom: `2px solid ${perlColors.border}`,
                            whiteSpace: "nowrap",
                            minWidth: 80,
                            maxWidth: 100,
                            padding: "6px 8px",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, fontSize: "0.8rem", lineHeight: 1.2 }}
                          >
                            Δ {valField}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "0.65rem", opacity: 0.8, display: "block" }}
                          >
                            {colHeader}
                          </Typography>
                        </TableCell>
                      )),
                    ])
                  : valueFields.flatMap((valField) => [
                      <TableCell
                        key={valField}
                        sx={{
                          bgcolor: "#2D2F31",
                          color: perlColors.textPrimary,
                          fontWeight: 600,
                          borderBottom: `2px solid ${perlColors.border}`,
                          whiteSpace: "nowrap",
                          minWidth: 100,
                          maxWidth: 120,
                          padding: "8px 12px",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                          {valField}
                        </Typography>
                      </TableCell>,
                      <TableCell
                        key={`${valField}_delta`}
                        sx={{
                          bgcolor: "#3D3F41",
                          color: "#ff6034",
                          fontWeight: 600,
                          borderBottom: `2px solid ${perlColors.border}`,
                          whiteSpace: "nowrap",
                          minWidth: 80,
                          maxWidth: 100,
                          padding: "8px 12px",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                          Δ
                        </Typography>
                      </TableCell>,
                    ])}
              </TableRow>
            </TableHead>
            <TableBody>
              {pivotRows.map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    height: "36px",
                    bgcolor: "transparent",
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.05)",
                    },
                  }}
                >
                  {/* Row field values */}
                  {rowFields.map((field) => (
                    <TableCell
                      key={field}
                      sx={{
                        color: perlColors.textPrimary,
                        borderBottom: `1px solid ${perlColors.border}`,
                        fontWeight: 600,
                        position: "sticky",
                        left: 0,
                        bgcolor: perlColors.background,
                        zIndex: 1,
                        padding: "8px 12px",
                        fontSize: "0.85rem",
                      }}
                    >
                      {row[field]}
                    </TableCell>
                  ))}

                  {/* Value cells with delta */}
                  {colFields.length > 0
                    ? // Group by value field: show all project values, then deltas
                      valueFields.flatMap((valField) => [
                        // Show all project values for this field
                        ...columnHeaders.map((colHeader) => {
                          const cellKey = `${colHeader}__${valField}`;
                          const value = row[cellKey];
                          return (
                            <TableCell
                              key={cellKey}
                              sx={{
                                color: perlColors.textPrimary,
                                borderBottom: `1px solid ${perlColors.border}`,
                                whiteSpace: "nowrap",
                                textAlign: "right",
                                padding: "8px 12px",
                                fontSize: "0.85rem",
                              }}
                            >
                              {typeof value === "number"
                                ? value.toLocaleString(undefined, {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 2,
                                  })
                                : value ?? "-"}
                            </TableCell>
                          );
                        }),
                        // Show delta columns for subsequent projects
                        ...columnHeaders.slice(1).map((colHeader) => {
                          const cellKey = `${colHeader}__${valField}`;
                          const delta = row[`${cellKey}_delta`];
                          const firstColumnHeader = columnHeaders[0];
                          const firstValue = row[`${firstColumnHeader}__${valField}`];
                          const percentDelta =
                            typeof delta === "number" &&
                            typeof firstValue === "number" &&
                            firstValue !== 0
                              ? (delta / firstValue) * 100
                              : null;

                          return (
                            <TableCell
                              key={`${cellKey}_delta`}
                              sx={{
                                color:
                                  delta > 0
                                    ? "#4caf50"
                                    : delta < 0
                                    ? "#f44336"
                                    : perlColors.textPrimary,
                                borderBottom: `1px solid ${perlColors.border}`,
                                whiteSpace: "nowrap",
                                textAlign: "right",
                                fontWeight: 600,
                                padding: "6px 8px",
                                fontSize: "0.85rem",
                              }}
                            >
                              {typeof delta === "number" ? (
                                delta === 0 ? (
                                  "- | -"
                                ) : (
                                  <>
                                    {(delta > 0 ? "+" : "") +
                                      delta.toLocaleString(undefined, {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2,
                                      })}
                                    {percentDelta !== null && (
                                      <span
                                        style={{
                                          fontSize: "0.75rem",
                                          opacity: 0.85,
                                          marginLeft: "4px",
                                        }}
                                      >
                                        | {(percentDelta > 0 ? "+" : "") + percentDelta.toFixed(1)}%
                                      </span>
                                    )}
                                  </>
                                )
                              ) : (
                                "-"
                              )}
                            </TableCell>
                          );
                        }),
                      ])
                    : valueFields.flatMap((valField) => {
                        const value = row[valField];
                        const delta = row[`${valField}_delta`];
                        return [
                          <TableCell
                            key={valField}
                            sx={{
                              color: perlColors.textPrimary,
                              borderBottom: `1px solid ${perlColors.border}`,
                              whiteSpace: "nowrap",
                              textAlign: "right",
                              padding: "8px 12px",
                              fontSize: "0.85rem",
                            }}
                          >
                            {typeof value === "number"
                              ? value.toLocaleString(undefined, {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                })
                              : value ?? "-"}
                          </TableCell>,
                          <TableCell
                            key={`${valField}_delta`}
                            sx={{
                              color:
                                delta > 0
                                  ? "#4caf50"
                                  : delta < 0
                                  ? "#f44336"
                                  : perlColors.textPrimary,
                              borderBottom: `1px solid ${perlColors.border}`,
                              whiteSpace: "nowrap",
                              textAlign: "right",
                              fontWeight: 600,
                              padding: "8px 12px",
                              fontSize: "0.85rem",
                            }}
                          >
                            {typeof delta === "number"
                              ? (delta > 0 ? "+" : "") +
                                delta.toLocaleString(undefined, {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                })
                              : "-"}
                          </TableCell>,
                        ];
                      })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName="Results"
          industry=""
          commodity="Database Output"
          tierImage="/images/Silver.jpg"
          tierLabel="Results Viewer"
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 1.5,
            borderBottom: `1px solid ${perlColors.border}`,
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            {/* Top Level Navigation */}
            <Tabs
              value={topLevelTab}
              onChange={(e, newValue) => setTopLevelTab(newValue)}
              sx={{
                minHeight: 36,
                "& .MuiTab-root": {
                  minHeight: 36,
                  color: perlColors.textPrimary,
                  opacity: 0.7,
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  "&.Mui-selected": {
                    color: "#ff6034",
                    opacity: 1,
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#ff6034",
                  height: 3,
                },
              }}
            >
              <Tab label="Calculation Results" />
              <Tab label="Analysis" />
            </Tabs>
          </Box>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => {
                if (newMode !== null) {
                  setViewMode(newMode);
                }
              }}
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  color: perlColors.textPrimary,
                  borderColor: perlColors.border,
                  "&.Mui-selected": {
                    bgcolor: "#ff6034",
                    color: "#fff",
                    "&:hover": {
                      bgcolor: "#e55530",
                    },
                  },
                  "&:hover": {
                    bgcolor: "rgba(255, 96, 52, 0.1)",
                  },
                },
              }}
            >
              <ToggleButton value="table" aria-label="table view">
                <Tooltip title="Table View">
                  <TableViewIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="card" aria-label="card view">
                <Tooltip title="Card View">
                  <CardViewIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            <FormControl size="small" sx={{ minWidth: 300 }}>
              <InputLabel sx={{ color: perlColors.textPrimary }}>Select Project</InputLabel>
              <Select
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                label="Select Project"
                disabled={loadingProjects || projectIds.length === 0}
                sx={{
                  color: perlColors.textPrimary,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: perlColors.border,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ff6034",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ff6034",
                  },
                  "& .MuiSvgIcon-root": {
                    color: perlColors.textPrimary,
                  },
                }}
              >
                {projectIds.map((project) => (
                  <MenuItem key={project.ProjectID} value={project.ProjectID}>
                    {project.ProjectID} - {project.ProductName || "N/A"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tooltip title="Refresh Results">
              <IconButton
                onClick={handleRefresh}
                disabled={loading || loadingProjects || !selectedProjectId}
                sx={{ color: perlColors.textPrimary }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Calculation Results View */}
        {topLevelTab === 0 && (
          <>
            <Box sx={{ borderBottom: `1px solid ${perlColors.border}`, px: 3, pt: 0, pb: 0 }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 42,
                  "& .MuiTab-root": {
                    color: perlColors.textPrimary,
                    opacity: 0.7,
                    "&.Mui-selected": {
                      color: "#ff6034",
                      opacity: 1,
                    },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#ff6034",
                  },
                }}
              >
                {resultTabs.map((tab) => (
                  <Tab key={tab.id} label={tab.label} />
                ))}
              </Tabs>
            </Box>

            <Box
              sx={{
                flex: 1,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                p: 0,
              }}
            >
              {viewMode === "table"
                ? renderTable(resultTabs[activeTab].key)
                : renderCardView(resultTabs[activeTab].key)}
            </Box>
          </>
        )}

        {/* Analysis View - Project Comparison */}
        {topLevelTab === 1 && (
          <>
            {/* Project Selector for Analysis */}

            {/* Analysis Tabs */}
            <Box sx={{ borderBottom: `1px solid ${perlColors.border}`, px: 3, pt: 0, pb: 0 }}>
              <Tabs
                value={analysisTab}
                onChange={(e, newValue) => setAnalysisTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 42,
                  "& .MuiTab-root": {
                    color: perlColors.textPrimary,
                    opacity: 0.7,
                    "&.Mui-selected": {
                      color: "#ff6034",
                      opacity: 1,
                    },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#ff6034",
                  },
                }}
              >
                {resultTabs.map((tab) => (
                  <Tab key={tab.id} label={tab.label} />
                ))}
              </Tabs>
            </Box>

            {/* Comparison View */}
            <Box
              sx={{
                flex: 1,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                p: 0,
              }}
            >
              {renderComparisonView()}
            </Box>
          </>
        )}

        {/* Column Settings Dialog */}
        <Dialog
          open={columnSettingsOpen}
          onClose={() => setColumnSettingsOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: perlColors.background,
              color: perlColors.textPrimary,
            },
          }}
        >
          <DialogTitle sx={{ borderBottom: `1px solid ${perlColors.border}` }}>
            Select Columns for {resultTabs[analysisTab].label}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Select which columns from <strong>{resultTabs[analysisTab].label}</strong> table you
              want to display in the comparison view. Each row will show a project's aggregated data
              for the selected columns.
            </Alert>
            <Typography
              variant="body2"
              sx={{ mb: 1, color: perlColors.textPrimary, fontWeight: 600 }}
            >
              {selectedColumns.length} of {availableColumns.length} columns selected
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                maxHeight: 400,
                overflow: "auto",
              }}
            >
              {availableColumns.map((col) => {
                return (
                  <Box
                    key={col}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 1,
                      borderRadius: 1,
                      bgcolor: "transparent",
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.05)",
                      },
                    }}
                  >
                    <Checkbox
                      checked={selectedColumns.includes(col)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedColumns([...selectedColumns, col]);
                        } else {
                          setSelectedColumns(selectedColumns.filter((c) => c !== col));
                        }
                      }}
                      sx={{
                        color: perlColors.textPrimary,
                        "&.Mui-checked": {
                          color: "#ff6034",
                        },
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 400 }}>{col}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </DialogContent>
          <DialogActions sx={{ borderTop: `1px solid ${perlColors.border}`, p: 2, gap: 1 }}>
            <Button
              onClick={() => {
                setSelectedColumns([]);
              }}
              sx={{ color: perlColors.textPrimary }}
            >
              Clear All
            </Button>
            <Button
              onClick={() => {
                setSelectedColumns(availableColumns);
              }}
              sx={{ color: perlColors.textPrimary }}
            >
              Select All
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={() => setColumnSettingsOpen(false)}
              sx={{ color: perlColors.textPrimary }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => setColumnSettingsOpen(false)}
              variant="contained"
              sx={{
                bgcolor: "#ff6034",
                "&:hover": { bgcolor: "#e55530" },
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        {/* Pivot Configuration Dialog */}
        <Dialog
          open={pivotConfigOpen}
          onClose={() => setPivotConfigOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: perlColors.background,
              color: perlColors.textPrimary,
            },
          }}
        >
          <DialogTitle sx={{ borderBottom: `1px solid ${perlColors.border}` }}>
            Configure Pivot Table for {resultTabs[analysisTab].label}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Configure your pivot table similar to Excel. Select row identifiers, column pivots,
              and values to aggregate.
            </Alert>
            <Grid container spacing={3}>
              {/* Rows Section */}
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: "rgba(255, 255, 255, 0.02)",
                    border: `1px solid ${perlColors.border}`,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, color: "#ff6034" }}>
                    Rows
                  </Typography>
                  <Typography variant="caption" sx={{ mb: 2, display: "block", opacity: 0.7 }}>
                    Select fields to use as row identifiers
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Row Fields</InputLabel>
                    <Select
                      multiple
                      value={pivotConfig.rows}
                      onChange={(e) => setPivotConfig({ ...pivotConfig, rows: e.target.value })}
                      input={<OutlinedInput label="Row Fields" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {["ProjectID", ...availableColumns].map((col) => (
                        <MenuItem key={col} value={col}>
                          <Checkbox checked={pivotConfig.rows.includes(col)} />
                          <ListItemText primary={col} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>

              {/* Columns Section */}
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: "rgba(255, 255, 255, 0.02)",
                    border: `1px solid ${perlColors.border}`,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, color: "#ff6034" }}>
                    Columns
                  </Typography>
                  <Typography variant="caption" sx={{ mb: 2, display: "block", opacity: 0.7 }}>
                    Select fields to pivot into columns (optional)
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Column Fields</InputLabel>
                    <Select
                      multiple
                      value={pivotConfig.columns}
                      onChange={(e) => setPivotConfig({ ...pivotConfig, columns: e.target.value })}
                      input={<OutlinedInput label="Column Fields" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {availableColumns.map((col) => (
                        <MenuItem key={col} value={col}>
                          <Checkbox checked={pivotConfig.columns.includes(col)} />
                          <ListItemText primary={col} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>

              {/* Values Section */}
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: "rgba(255, 255, 255, 0.02)",
                    border: `1px solid ${perlColors.border}`,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, color: "#ff6034" }}>
                    Values
                  </Typography>
                  <Typography variant="caption" sx={{ mb: 2, display: "block", opacity: 0.7 }}>
                    Select numeric fields to aggregate (sum)
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Value Fields</InputLabel>
                    <Select
                      multiple
                      value={pivotConfig.values}
                      onChange={(e) => setPivotConfig({ ...pivotConfig, values: e.target.value })}
                      input={<OutlinedInput label="Value Fields" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {availableColumns.map((col) => (
                        <MenuItem key={col} value={col}>
                          <Checkbox checked={pivotConfig.values.includes(col)} />
                          <ListItemText primary={col} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ borderTop: `1px solid ${perlColors.border}`, p: 2, gap: 1 }}>
            <Button
              onClick={() => {
                setPivotConfig({ rows: [], columns: [], values: [] });
              }}
              sx={{ color: perlColors.textPrimary }}
            >
              Reset
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={() => setPivotConfigOpen(false)}
              sx={{ color: perlColors.textPrimary }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => setPivotConfigOpen(false)}
              variant="contained"
              sx={{
                bgcolor: "#ff6034",
                "&:hover": { bgcolor: "#e55530" },
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        {/* Project Selector Dialog */}
        <Dialog
          open={projectSelectorOpen}
          onClose={() => setProjectSelectorOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: perlColors.background,
              color: perlColors.textPrimary,
            },
          }}
        >
          <DialogTitle sx={{ borderBottom: `1px solid ${perlColors.border}` }}>
            Select Projects
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: perlColors.textPrimary }}>Projects</InputLabel>
              <Select
                multiple
                value={selectedProjects}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedProjects(typeof value === "string" ? value.split(",") : value);
                }}
                input={<OutlinedInput label="Projects" />}
                renderValue={(selected) => `${selected.length} project(s) selected`}
                sx={{
                  color: perlColors.textPrimary,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: perlColors.border,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ff6034",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ff6034",
                  },
                }}
              >
                {projectIds.map((project) => (
                  <MenuItem key={project.ProjectID} value={project.ProjectID}>
                    <Checkbox checked={selectedProjects.indexOf(project.ProjectID) > -1} />
                    <ListItemText
                      primary={project.ProjectID}
                      secondary={project.ProductName || "N/A"}
                      primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: 500 }}
                      secondaryTypographyProps={{ fontSize: "0.75rem" }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedProjects.length > 0 && (
              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selectedProjects.map((projectId) => (
                  <Chip
                    key={projectId}
                    label={projectId}
                    size="small"
                    onDelete={() => {
                      setSelectedProjects(selectedProjects.filter((id) => id !== projectId));
                    }}
                    sx={{
                      bgcolor: "#ff6034",
                      color: "#fff",
                      "& .MuiChip-deleteIcon": { color: "#fff" },
                    }}
                  />
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ borderTop: `1px solid ${perlColors.border}`, p: 2 }}>
            <Button
              onClick={() => setProjectSelectorOpen(false)}
              sx={{ color: perlColors.textPrimary }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SidebarLayout>
  );
}
